import React, { useState, useCallback, useEffect } from 'react';
import { Modal, TextInput, Textarea, Button, Stack, Group } from '@mantine/core';
import { IconPlus, IconX } from '@tabler/icons-react';
import { CreateRegionCommand } from '@/domain/entities/Region';
import { useCreateRegionMutation } from '@/application/queries/regionQueries';
import { useAuth } from '../../../hooks/useAuth';

export interface CreateRegionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

/**
 * CreateRegionModal - Modal for creating a new region
 *
 * Features:
 * - Form with required fields (regionId, name, country)
 * - Optional fields (state, city, description, subreddits)
 * - Form validation (required fields, max lengths, regionId format)
 * - Success/error notifications via mutation hook
 * - Loading state during save
 * - Note: SubAreas must be added separately after Region creation
 *
 * Usage:
 * - User creates Region with basic information
 * - After creation, navigate to Region Detail page to add SubAreas
 * - Region requires at least 1 SubArea to be fully operational
 */
export const CreateRegionModal: React.FC<CreateRegionModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}): React.ReactElement => {
  const { user } = useAuth();
  const createRegionMutation = useCreateRegionMutation();

  // Form state - initialized with empty values
  const [formData, setFormData] = useState<CreateRegionCommand>({
    regionId: '',
    name: '',
    country: '',
    state: undefined,
    city: undefined,
    description: undefined,
    subreddits: undefined,
    isActive: true
  });

  // Form validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  /**
   * Reset form when modal opens
   */
  useEffect(() => {
    if (isOpen) {
      setFormData({
        regionId: '',
        name: '',
        country: '',
        state: undefined,
        city: undefined,
        description: undefined,
        subreddits: undefined,
        isActive: true
      });
      setErrors({});
    }
  }, [isOpen]);

  /**
   * Handle form field changes
   */
  const handleChange = useCallback((field: keyof CreateRegionCommand, value: string): void => {
    setFormData((prev) => ({
      ...prev,
      [field]: value === '' ? undefined : value
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  }, [errors]);

  /**
   * Validate form
   */
  const validate = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    // Region ID is required (human-readable ID like "seattle")
    if (!formData['regionId'] || formData['regionId'].trim() === '') {
      newErrors['regionId'] = 'Region ID is required';
    } else if (formData['regionId'].length > 50) {
      newErrors['regionId'] = 'Region ID must be 50 characters or less';
    } else if (!/^[a-z0-9-]+$/.test(formData['regionId'])) {
      newErrors['regionId'] = 'Region ID must contain only lowercase letters, numbers, and hyphens';
    }

    // Region name is required
    if (!formData['name'] || formData['name'].trim() === '') {
      newErrors['name'] = 'Region name is required';
    } else if (formData['name'].length > 100) {
      newErrors['name'] = 'Region name must be 100 characters or less';
    }

    // Country is required
    if (!formData['country'] || formData['country'].trim() === '') {
      newErrors['country'] = 'Country is required';
    } else if (formData['country'].length > 100) {
      newErrors['country'] = 'Country must be 100 characters or less';
    }

    // Optional fields max length validation
    if (formData['state'] && formData['state'].length > 100) {
      newErrors['state'] = 'State must be 100 characters or less';
    }

    if (formData['city'] && formData['city'].length > 100) {
      newErrors['city'] = 'City must be 100 characters or less';
    }

    if (formData['description'] && formData['description'].length > 500) {
      newErrors['description'] = 'Description must be 500 characters or less';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback(async (): Promise<void> => {
    if (!validate()) {
      return;
    }

    try {
      await createRegionMutation.mutateAsync({
        command: formData,
        userRole: user?.role ?? 'BusinessOwner'
      });

      // Close modal and notify parent
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      // Error handling is done by the mutation hook (toast notification)
      console.error('Failed to create region:', error);
    }
  }, [validate, createRegionMutation, formData, user?.role, onClose, onSuccess]);

  /**
   * Handle modal close
   */
  const handleClose = useCallback((): void => {
    if (!createRegionMutation.isPending) {
      onClose();
    }
  }, [createRegionMutation.isPending, onClose]);

  return (
    <Modal
      opened={isOpen}
      onClose={handleClose}
      title="Create New Region"
      size="lg"
      centered
      closeOnClickOutside={!createRegionMutation.isPending}
      closeOnEscape={!createRegionMutation.isPending}
    >
      <Stack gap="md">
        {/* Region ID */}
        <TextInput
          label="Region ID"
          placeholder="seattle"
          description="Lowercase, no spaces (e.g., 'seattle', 'boston-metro')"
          value={formData['regionId']}
          onChange={(e) => handleChange('regionId', e.currentTarget.value.toLowerCase())}
          error={errors['regionId']}
          required
          disabled={createRegionMutation.isPending}
        />

        {/* Region Name */}
        <TextInput
          label="Region Name"
          placeholder="Seattle Metro"
          description="Display name for the region"
          value={formData['name']}
          onChange={(e) => handleChange('name', e.currentTarget.value)}
          error={errors['name']}
          required
          disabled={createRegionMutation.isPending}
        />

        {/* Country */}
        <TextInput
          label="Country"
          placeholder="United States"
          value={formData['country']}
          onChange={(e) => handleChange('country', e.currentTarget.value)}
          error={errors['country']}
          required
          disabled={createRegionMutation.isPending}
        />

        {/* State */}
        <TextInput
          label="State"
          placeholder="Washington"
          value={formData['state'] ?? ''}
          onChange={(e) => handleChange('state', e.currentTarget.value)}
          error={errors['state']}
          disabled={createRegionMutation.isPending}
        />

        {/* City */}
        <TextInput
          label="City"
          placeholder="Seattle"
          value={formData['city'] ?? ''}
          onChange={(e) => handleChange('city', e.currentTarget.value)}
          error={errors['city']}
          disabled={createRegionMutation.isPending}
        />

        {/* Description */}
        <Textarea
          label="Description"
          placeholder="Greater Seattle metropolitan area..."
          value={formData['description'] ?? ''}
          onChange={(e) => handleChange('description', e.currentTarget.value)}
          error={errors['description']}
          minRows={3}
          maxRows={5}
          disabled={createRegionMutation.isPending}
        />

        {/* Actions */}
        <Group justify="flex-end" mt="md">
          <Button
            variant="subtle"
            leftSection={<IconX size={16} />}
            onClick={handleClose}
            disabled={createRegionMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={handleSubmit}
            loading={createRegionMutation.isPending}
          >
            Create Region
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
